import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot } from '@angular/router';
import { EMPTY, mergeMap, Observable, of, take } from 'rxjs';
import { RealEstateService } from '@app/core/services/realEstate/real-estate.service';
import {
  RealEstateDTO,
  RebuildEstateDTO,
  TargetAdditionStoryDTO,
  TargetAnnexDTO,
  TargetOverhaulDTO,
} from '@generated/generatedEntities';
import { AuthenticationService, CredentialsService } from '@app/auth';
import { catchError, switchMap, tap, map } from 'rxjs/operators';
import { Logger } from '@app/@shared';
import { SmallBuildingRestService } from '@app/calculators/services/small-building-service/small-building-rest.service';
import { RebuildEstateDataService } from '../rebuild/rebuild-estate-data/rebuild-estate-data.service';
import { RebuildEstateService } from '../rebuild/rebuild-estate.service';
import { LccDataService } from '@app/calculators/lcc-form/lcc-data.service';
import { PresetWrapperService } from '@app/calculators/services/preset/preset-wrapper.service';
import { IframeInput } from '@generated/extended';
import { RealEstateRepository } from '@app/core/services/state/realEstate/real-estate.repository';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RealEstateActions } from '@app/core/services/store/real-estate.actions';
import _ from 'lodash';

const log = new Logger('RebuildEstateResolver');
type STATE = 'STATE' | 'SESSIONSTORAGE' | 'LOCALSTORAGE';
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RealEstateResolverResolver {
  private realEstateService = inject(RealEstateService);
  private router = inject(Router);
  private credentialsService = inject(CredentialsService);
  private authenticationService = inject(AuthenticationService);
  private _smallBuildingService = inject(SmallBuildingRestService);
  private _rebuildEstateService = inject(RebuildEstateService);
  private _rebuildEstateDataService = inject(RebuildEstateDataService);
  private lccDataService = inject(LccDataService);
  private presetWrapperService = inject(PresetWrapperService);
  private realEstateRepository = inject(RealEstateRepository);
  private store = inject(Store);

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<RealEstateDTO | RebuildEstateDTO | IframeInput> {
    let realEstateByStateParam: RealEstateDTO;
    const realEstateIdByStateParam =
      (getStateByKey('STATE', 'realEstateId', this.router) ?? getStateByKey('SESSIONSTORAGE', 'realEstateId'))
        ? parseInt(getStateByKey('SESSIONSTORAGE', 'realEstateId'))
        : null;
    let realEstate = getStateByKey('STATE', 'realEstate', this.router);
    this.store.dispatch(RealEstateActions.resetStore());

    const realestateReferenceObjectExists = getStateByKey('STATE', 'referenceObject', this.router) ?? false;
    if (realestateReferenceObjectExists) {
      log.info('[RealEstate Reference] reference: ', realestateReferenceObjectExists);
      const _withdrawalProductTypes =
        getStateByKey('STATE', 'withdrawalProductTypes', this.router).toLocaleUpperCase() ?? '';
      return this.realEstateService.getEmptyRealEstate(_withdrawalProductTypes).pipe(
        switchMap((realEstateDTO) => {
          realEstateDTO.quality.qualitaetGrundstueck = realEstate.quality.qualitaetGrundstueck;
          realEstateDTO.quality.qualitaetGrundstueckBackend = realEstate.quality.qualitaetGrundstueckBackend;
          realEstateDTO.quality.qualitaetvorbereitungsarbeiten = realEstate.quality.qualitaetvorbereitungsarbeiten;
          realEstateDTO.quality.qualitaetvorbereitungsarbeitenBackend =
            realEstate.quality.qualitaetvorbereitungsarbeitenBackend;
          realEstateDTO.quality.qualitaetVorfertigung = realEstate.quality.qualitaetVorfertigung;
          realEstateDTO.quality.qualitaetVorfertigungBackend = realEstate.quality.qualitaetVorfertigungBackend;
          realEstateDTO.quality.qualitaetDerGebaeudeform2 = realEstate.quality.qualitaetDerGebaeudeform2;
          realEstateDTO.quality.qualitaetDerGebaeudeform2Backend = realEstate.quality.qualitaetDerGebaeudeform2Backend;
          realEstateDTO.quality.fensterAnteil = realEstate.quality.fensterAnteil;
          realEstateDTO.quality.fensterAnteilBackend = realEstate.quality.fensterAnteilBackend;
          realEstateDTO.quality.qualitaetElektroAnlage = realEstate.quality.qualitaetElektroAnlage;
          realEstateDTO.quality.qualitaetElektroAnlageBackend = realEstate.quality.qualitaetElektroAnlageBackend;
          realEstateDTO.quality.qualitaetHeizungsanlage = realEstate.quality.qualitaetHeizungsanlage;
          realEstateDTO.quality.qualitaetHeizungsanlageBackend = realEstate.quality.qualitaetHeizungsanlageBackend;
          realEstateDTO.quality.qualitaetSanitaeranlage = realEstate.quality.qualitaetSanitaeranlage;
          realEstateDTO.quality.qualitaetSanitaeranlageBackend = realEstate.quality.qualitaetSanitaeranlageBackend;
          realEstateDTO.quality.qualitaetAusbau1 = realEstate.quality.qualitaetAusbau1;
          realEstateDTO.quality.qualitaetAusbau1Backend = realEstate.quality.qualitaetAusbau1Backend;
          realEstateDTO.quality.qualitaetAusbau2 = realEstate.quality.qualitaetAusbau2;
          realEstateDTO.quality.qualitaetAusbau2Backend = realEstate.quality.qualitaetAusbau2Backend;
          realEstateDTO.quality.qualitaetUmgebung = realEstate.quality.qualitaetUmgebung;
          realEstateDTO.quality.qualitaetUmgebungBackend = realEstate.quality.qualitaetUmgebungBackend;

          return of(realEstateDTO);
        }),
      );
    }
    if (getStateByKey('LOCALSTORAGE', 'replacementRealEstate') && route.queryParams['rebuildReplacement']) {
      realEstate = JSON.parse(getStateByKey('LOCALSTORAGE', 'replacementRealEstate'));
      this.presetWrapperService.realEstate = realEstate; // for fetching the reference(roofType)
      this.presetWrapperService
        .setCorrectSliderModelValues2(realEstate.usages)
        .pipe(untilDestroyed(this), take(1))
        .subscribe({});
      //local storage must be cleared when data is consumed (otherwise it will not work) - lufon.
      this.realEstateService.realEstateContainer = realEstate;
      realEstateByStateParam = realEstate;
      return of(realEstate);
    } else {
      realEstateByStateParam = realEstate;
      return this.loadRealEstate(realEstateIdByStateParam, realEstateByStateParam, state, route.queryParams).pipe(
        switchMap((realEstate) => {
          return of(realEstate);
        }),
        catchError(() => {
          return EMPTY;
        }),
      );
    }
  }

  private loadRealEstate<T extends RealEstateDTO, K extends number | null>(
    realEstateIdByStateParam: K,
    realEstateByStateParam: T,
    state: RouterStateSnapshot,
    queryParams: Params,
  ): Observable<RealEstateDTO | IframeInput> {
    if (this.credentialsService.isAuthenticated()) {
      if (realEstateIdByStateParam && realEstateByStateParam) {
        log.debug('Requested both realEstateIdByStateParam and realEstateByStateParam.. routing couldnt be resolved');
        // this might happen if user loads a reference object (input new) and then edits this object
        // basically, frontend should make sure that realEstate stateParam is not set in this case, but it's quite hard to reach
        this.router.navigate(['/'], { replaceUrl: true });
        return EMPTY;
      }
      // public module routes are routed through this condition along with requestId.
      if (!!queryParams['requestId']) {
        log.info('public mode with requestId: ', queryParams['requestId']);
        return this.resolvePublicModuleRoutes(state, queryParams);
      }
      // module route 'edit' is routed through this condition along with realEstateId.
      if (realEstateIdByStateParam) {
        return this.resolveModuleRoutes(state, realEstateIdByStateParam);
      }
      // module routes 'input & debug' are routed through this condition.
      else {
        sessionStorage.removeItem('realEstateId');
        return this.resolveModuleRoutes(state);
      }
    }
    // user isn't properly authenticated, should be logged out and storage should be cleared?
    else {
      log.error(`User is not properly authenticated.`);
      sessionStorage.removeItem('realEstateId');
      localStorage.removeItem('realEstateId');
      sessionStorage.removeItem('debugState');
      this.authenticationService.logout();
      this.router.navigate(['/'], { replaceUrl: true });
      return EMPTY;
    }
  }

  private resolvePublicModuleRoutes(
    state: RouterStateSnapshot,
    queryParams: Params,
  ): Observable<RealEstateDTO | IframeInput> {
    const stateUrl = state.url;
    if (
      stateUrl.includes('public-process-form/edit') ||
      stateUrl.includes('public-rebuild-form/edit') ||
      stateUrl.includes('public-insurance-form/edit') ||
      stateUrl.includes('public-operational-form/edit')
    ) {
      return this.realEstateService.getRealEstateByRequestId(queryParams['requestId']);
    }
    if (stateUrl.includes('public-lcc-form/edit')) {
      log.debug('queryParams', queryParams['requestId']);
      return this.realEstateService.getRealEstateByRequestId(queryParams['requestId']).pipe(
        take(1),
        tap((value) => {
          return this.lccDataService.processEntity(value);
        }),
      );
    }
    if (stateUrl.includes('public-lca-form/edit')) {
      log.warn('queryParams', queryParams['requestId']);
      const ret = this.realEstateService.getRealEstateByRequestId(queryParams['requestId']);
      ret.pipe(take(1)).subscribe({
        next: (value) => {
          this.realEstateService.realEstateContainer = value;
          this.store.dispatch(RealEstateActions.updateRealEstate(value));
        },
      });
      //we need to fix this so we get a correct realEstate so we render the form.
      // this.store.dispatch(RealEstateActions.updateRealEstate({ requestId: queryParams['requestId'] }));
      return ret;
      // return this.realEstateRepository.get(queryParams['requestId']);
    } else {
      this.router.navigate(['/'], { replaceUrl: true });
      return EMPTY;
    }
  }

  private resolveModuleRoutes(
    state: RouterStateSnapshot,
    realEstateIdByStateParam?: number,
  ): Observable<RealEstateDTO | IframeInput> {
    const [, module, action] = state.url.split('/');
    log.info('[mutation] module:', module);
    log.info('[mutation] action:', action);
    switch (action) {
      case 'ep-input':
        switch (module) {
          case 'process-form':
            return this.realEstateService.getEmptyRealEstate('BUILDING_COSTS_AND_DATES');
          case 'rebuild-form':
            return this._rebuildEstateDataService.getEmpty();
          default:
            this.router.navigate(['/'], { replaceUrl: true });
            return EMPTY;
        }
      case 'input':
        switch (module) {
          case 'process-form':
            return this.realEstateService.getEmptyRealEstate('BUILDING_COSTS_AND_DATES');
          case 'operational-form':
            return this.realEstateService.getEmptyRealEstate('OPERATING_COSTS');
          case 'rebuild-form':
            return this._rebuildEstateDataService.getEmpty();
          // case 'planning-foundation':
          //   break;
          case 'lcc-form':
            return this.lccDataService.getEmpty();
          case 'lca-form':
            return this.realEstateRepository.getEmpty('LCA', ['LCA']);
          // case 'effort-planner-form':
          //   break;
          case 'kv-to-bim':
            return this.realEstateRepository.getEmpty('BIM_EVALUATION', ['BUILDING_COSTS_AND_DATES', 'BIM_EVALUATION']);
          case 'insurance-form':
            return this.realEstateService.getEmptyRealEstate('BUILDING_INSURANCE_VALUES');
          case 'gvbs':
            return this.realEstateService.getEmptyRealEstate('INSURANCE_VALUES_GVBS');
          case 'agv-small-building-form':
            return this._smallBuildingService.getEmptyRealEstate();
          case 'damage-form':
            return this._rebuildEstateDataService.getDamageEmpty();
          case 'small-building-gvbs-form':
            return this._smallBuildingService.getEmptyRealEstate('SMALL_BUILDING_GVBS');
          default:
            // return EMPTY
            return this.realEstateService.getEmptyRealEstate('BUILDING_COSTS_AND_DATES');
        }
      case 'edit':
        if (realEstateIdByStateParam) {
          log.info('[mutation] args:', realEstateIdByStateParam);
          switch (module) {
            case 'process-form':
              return this.realEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this.realEstateService.postProcessForEditRecordById(realEstate);
                }),
              );
            case 'operational-form':
              return this.realEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this.realEstateService.postProcessForEditRecordById(realEstate);
                }),
              );
            case 'rebuild-form':
              return this._rebuildEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  // console.log('reached next',realEstate);
                  if (
                    realEstate.metaData.tasks.includes('REBUILD_COSTS') &&
                    realEstate.metaData.tasks.includes('LCA')
                  ) {
                    if (realEstate.energy != null && realEstate.energy.id != null) {
                      realEstate.energy.id = null;
                    }
                    if (
                      realEstate.energy != null &&
                      realEstate.energy.current != null &&
                      realEstate.energy.current.id != null
                    ) {
                      realEstate.energy.current.id = null;
                    }
                    if (
                      realEstate.energy != null &&
                      realEstate.energy.target != null &&
                      realEstate.energy.target.id != null
                    ) {
                      realEstate.energy.target.id = null;
                    }
                    this.store.dispatch(RealEstateActions.updateRealEstate(_.cloneDeep(realEstate)));
                  }
                  return this._rebuildEstateDataService.postProcessForEditRecordById(realEstate);
                }),
              );
            // case 'operational-form':
            //  break;
            // case 'planning-foundation':
            //   break;
            case 'lcc-form':
              return this.lccDataService.get(realEstateIdByStateParam);
            case 'lca-form':
              return this.realEstateRepository.get(realEstateIdByStateParam);
            // case 'effort-planner-form':
            //   break;
            case 'kv-to-bim':
              return this.realEstateRepository.get(realEstateIdByStateParam);
            // case 'insurance-form':
            //   break;
            case 'gvbs':
              return this.realEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => this.realEstateService.postProcessForEditRecordById(realEstate)),
                map((processed) => {
                  processed.investments = [];
                  return processed;
                }),
              )
            case 'agv-small-building-form':
              return this._smallBuildingService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this._smallBuildingService.postProcessForEditRecordById(realEstate);
                }),
              );
            case 'damage-form':
              return this._rebuildEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this._rebuildEstateDataService.postProcessForEditRecordById(realEstate);
                }),
              );
            case 'small-building-gvbs-form':
              return this._smallBuildingService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this._smallBuildingService.postProcessForEditRecordById(realEstate);
                }),
              );
            default:
              // return EMPTY
              return this.realEstateService.getRealEstateById(realEstateIdByStateParam).pipe(
                take(1),
                mergeMap((realEstate) => {
                  return this.realEstateService.postProcessForEditRecordById(realEstate);
                }),
              );
          }
        } else {
          log.error('valid realEstateIdBy not found. realEstateIdByStateParam: ', realEstateIdByStateParam);
          this.router.navigate(['/'], { replaceUrl: true });
          return EMPTY;
        }
      case 'debug':
        const _debugInstanceExists = getStateByKey('SESSIONSTORAGE', 'debugState');
        if (_debugInstanceExists) {
          const _debugInsance = JSON.parse(_debugInstanceExists);
          const realestate = _debugInsance;
          log.info('[mutation] args:', realestate);

          switch (module) {
            case 'process-form':
              return this.realEstateService.getEmptyRealEstate('BUILDING_COSTS_AND_DATES').pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'rebuild-form':
              return this._rebuildEstateDataService.getEmpty().pipe(
                mergeMap((response) => {
                  if (!realestate.targetAdditionStory) {
                    realestate.targetAdditionStory = _.cloneDeep({} as TargetAdditionStoryDTO);
                  }
                  if (!realestate.targetAnnex) {
                    realestate.targetAnnex = _.cloneDeep({} as TargetAnnexDTO);
                  }
                  if (!realestate.targetOverhaul) {
                    realestate.targetOverhaul = _.cloneDeep({} as TargetOverhaulDTO);
                  }
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'operational-form':
              return this.realEstateService.getEmptyRealEstate('OPERATING_COSTS').pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            // case 'planning-foundation':
            //   break;
            case 'lcc-form':
              return this.lccDataService.getEmpty().pipe(
                tap((response) => {
                  response = _.cloneDeep(realestate);
                  return this.store.dispatch(RealEstateActions.updateRealEstate(_.cloneDeep(response)));
                }),
              );

            case 'lca-form':
              return this.realEstateRepository.getEmpty('LCA', ['LCA']).pipe(
                tap({
                  next: (response) => {
                    response = _.cloneDeep(realestate);
                    this.realEstateService.realEstateContainer = response;
                    return this.store.dispatch(
                      RealEstateActions.updateRealEstate(JSON.parse(JSON.stringify(response))),
                    );
                  },
                }),
              );
            // case 'effort-planner-form':
            //   break;
            // case 'kv-to-bim':
            //   return this.realEstateRepository.getEmpty('LCA', ['BUILDING_COSTS_AND_DATES', 'LCA', 'BIM_EVALUATION']).pipe(
            //     take(1),
            //     mergeMap((response) => {
            //       response = _.cloneDeep(realestate);
            //       return of(response);
            //     }),
            //   );
            case 'insurance-form':
              return this.realEstateService.getEmptyRealEstate('BUILDING_INSURANCE_VALUES').pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'gvbs':
              return this.realEstateService.getEmptyRealEstate('INSURANCE_VALUES_GVBS').pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'agv-small-building-form':
              return this._smallBuildingService.getEmptyRealEstate().pipe(
                take(1),
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'damage-form':
              return this._rebuildEstateDataService.getDamageEmpty().pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            case 'small-building-gvbs-form':
              return this._smallBuildingService.getEmptyRealEstate('SMALL_BUILDING_GVBS').pipe(
                take(1),
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
            default:
              return this.realEstateService.getEmptyRealEstate('BUILDING_COSTS_AND_DATES').pipe(
                mergeMap((response) => {
                  response = _.cloneDeep(realestate);
                  return of(response);
                }),
              );
          }
        } else {
          log.warn("couldn't find debug state. Please reupload json file. ");
          this.router.navigate(['/'], { replaceUrl: true });
          return EMPTY;
        }
      default:
        log.error(`Modules route couldn't be resolved`);
        this.router.navigate(['/'], { replaceUrl: true });
        return EMPTY;
    }
  }
}

export function getStateByKey(state: STATE, key: string, router?: Router) {
  switch (state) {
    case 'STATE':
      return router?.getCurrentNavigation()?.extras.state?.[key];
    case 'SESSIONSTORAGE':
      return sessionStorage.getItem(key);
    case 'LOCALSTORAGE':
      return localStorage.getItem(key);
  }
}
